import { ChevronRight, MapPin, Star } from 'lucide-react';
import Badge from 'components/badge/badge';
import { sectorBadgeType } from 'constants/companySectors';
import {
  formatTemplateCardRate,
  formatTemplateCity,
  getTemplateQualification,
  getTemplateTitle,
} from './missionTemplateFormat';

/** Small outlined tag of the list (a count). */
export const ListTag = ({ children }) => (
  <span className="inline-flex shrink-0 items-center justify-center rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium leading-4 text-gray-700">
    {children}
  </span>
);

/**
 * One job description of a list, as on the company front's « Fiches de poste »
 * (company/src/pages/missionTemplates/components/TemplateRow.js): star, name
 * and qualification, city, hourly rate and its basis, sector, chevron. One
 * below the minimum wage turns red. Without `onOpen`, it is only read.
 */
const TemplateRow = ({ template, onOpen }) => {
  if (!template) return null;
  const isBelowMinimumWage = !!template.isBelowMinimumWage;
  const rate = formatTemplateCardRate(template);
  const qualification = getTemplateQualification(template);
  const city = formatTemplateCity(template.location);
  const opening = onOpen
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: () => onOpen(template),
        onKeyDown: (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          // Space would otherwise scroll the page.
          event.preventDefault();
          onOpen(template);
        },
      }
    : {};

  return (
    <div
      {...opening}
      className={`flex items-center gap-4 border-b border-gray-100 bg-white px-5 py-3.5 last:border-b-0 ${
        onOpen ? 'cursor-pointer hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50' : ''
      }`}
    >
      <div
        className={`corner-smooth-8 flex h-10 w-10 shrink-0 items-center justify-center ${
          isBelowMinimumWage ? 'bg-red-50' : 'bg-gray-100'
        }`}
      >
        <Star size={20} color={isBelowMinimumWage ? '#B91C1C' : '#374151'} aria-hidden="true" />
      </div>

      {/* The fixed columns appear only where the list is wide enough, so the name always shows. */}
      <div className="flex min-w-[120px] flex-1 flex-col gap-0.5">
        <p className="truncate text-sm font-medium leading-5 text-gray-900">{getTemplateTitle(template) || '-'}</p>
        {qualification ? <p className="truncate text-xs leading-4 text-gray-700">{qualification}</p> : null}
      </div>

      <div className="hidden w-[180px] shrink-0 items-center gap-1.5 lg:flex">
        <MapPin size={16} color="#6B7280" aria-hidden="true" />
        <span className="truncate text-xs leading-4 text-gray-700">{city || '-'}</span>
      </div>

      <div className="flex w-[140px] shrink-0 flex-col gap-0.5">
        {rate ? (
          <p className="flex items-end gap-0.5 whitespace-nowrap">
            <span className={`text-sm font-medium leading-5 ${isBelowMinimumWage ? 'text-red-700' : 'text-gray-900'}`}>
              {rate}
            </span>
            <span className="text-xs leading-4 text-gray-500">/h</span>
          </p>
        ) : null}
        <p className={`text-xs leading-4 ${isBelowMinimumWage ? 'text-red-700' : 'text-gray-700'}`}>
          {isBelowMinimumWage
            ? 'Sous le SMIC'
            : template.isIndexedToMinimumWage
              ? 'Indexé au SMIC'
              : 'Taux horaire brut'}
        </p>
      </div>

      <div className="hidden h-6 w-[160px] shrink-0 items-center justify-end xl:flex">
        {template.activity ? <Badge type={sectorBadgeType(template.activity)}>{template.activity}</Badge> : null}
      </div>

      {onOpen ? <ChevronRight size={20} color="#374151" className="shrink-0" aria-hidden="true" /> : null}
    </div>
  );
};

export default TemplateRow;
